import { eq } from 'drizzle-orm'
import { z } from 'zod'
import { assistantRejectedFingerprintInputSchema } from '../../assistantSchemas'
import {
  assistantProposalTable,
  assistantRejectedFingerprintTable,
} from '../../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../../types'
import { parseAssistantJsonObject } from './assistantWriteUtils'

/** browser再検証結果を表す個別拒否入力schema。 */
export const assistantProposalRejectionSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('valid'),
      fingerprint: assistantRejectedFingerprintInputSchema,
    })
    .strict(),
  z.object({ status: z.literal('invalid') }).strict(),
])

export type AssistantProposalRejection = z.infer<
  typeof assistantProposalRejectionSchema
>

/**
 * 1提案を拒否し、有効提案の場合だけfingerprintを同じtransactionで保存する。
 * @param params DB、proposal ID、browserで再検証した有効性とfingerprint。
 * @returns transaction内で実際に削除したproposal ID。
 */
export const rejectAssistantProposal = async ({
  database: { transaction },
  proposalId,
  rejection,
  validate,
}: {
  readonly database: DatabaseWithTransaction
  readonly proposalId: string
  readonly rejection: AssistantProposalRejection
  readonly validate?: (parameters: {
    readonly database: TransactionDatabase
    readonly proposalId: string
  }) => Promise<AssistantProposalRejection>
}): Promise<readonly string[]> => {
  const parsedProposalId = z.string().min(1).parse(proposalId)
  let parsedRejection = assistantProposalRejectionSchema.parse(rejection)
  let removedProposalIds: readonly string[] = []

  await transaction([
    (tx) => [
      (async () => {
        const proposalRows = await tx
          .select({
            ruleKey: assistantProposalTable.ruleKey,
            ruleVersion: assistantProposalTable.ruleVersion,
          })
          .from(assistantProposalTable)
          .where(eq(assistantProposalTable.id, parsedProposalId))
          .limit(1)
        if (proposalRows.length === 0) {
          throw new Error('assistant proposal is not found')
        }
        parsedRejection = assistantProposalRejectionSchema.parse(
          (await validate?.({
            database: tx,
            proposalId: parsedProposalId,
          })) ?? parsedRejection,
        )
        if (parsedRejection.status === 'valid') {
          parseAssistantJsonObject({
            json: parsedRejection.fingerprint.revalidationPayloadJson,
            label: 'assistant rejection revalidation payload',
          })
        }
        if (
          parsedRejection.status === 'valid' &&
          (parsedRejection.fingerprint.ruleKey !== proposalRows[0].ruleKey ||
            parsedRejection.fingerprint.ruleVersion !==
              proposalRows[0].ruleVersion)
        ) {
          throw new Error('assistant rejection rule does not match proposal')
        }
      })(),
    ],
    (tx) =>
      parsedRejection.status === 'valid'
        ? [
            tx
              .insert(assistantRejectedFingerprintTable)
              .values({
                ...parsedRejection.fingerprint,
                createdAt: new Date(),
              })
              .onConflictDoNothing(),
          ]
        : [],
    (tx) => [
      tx
        .delete(assistantProposalTable)
        .where(eq(assistantProposalTable.id, parsedProposalId))
        .returning({ id: assistantProposalTable.id })
        .then((rows) => {
          removedProposalIds = rows.map((row) => row.id)
        }),
    ],
  ])
  return removedProposalIds
}
